<template>
  <div class="d-flex flex-row justify-center zoom-container">
    <v-card
      elevation="0"
      class="d-flex flex-row align-center zoom-control text-white text-body-small"
      color="black"
      rounded="lg"
    >
      <template v-if="!isLoading">
        <template v-if="Number(numPages) > 0">
          <div class="zoom-pages pl-3">
            {{
              t('documents.preview.node_actions.pages', {
                currentPage: currentPage,
                totalPages: numPages,
              })
            }}
          </div>
          <div class="separator"></div>
        </template>
        <template v-if="!hideZoom">
          <div class="zoom-buttons-group cursor-pointer">
            <div class="px-2 py-5" @click="emit('click:zoom-out')">
              <v-icon
                icon="far fa-circle-minus"
                class="zoom-icon-minus"
                :color="zoom === minZoom ? '#868E96' : 'white'"
              />
            </div>
            <div
              v-if="!hideZoomPercentage"
              class="d-flex flex-column justify-center zoom-percentage"
            >
              {{ zoomPercentage }}%
            </div>
            <div class="px-2 py-5" @click="emit('click:zoom-in')">
              <v-icon
                icon="far fa-circle-plus"
                class="zoom-icon-plus"
                :color="zoom === maxZoom ? '#868E96' : 'white'"
              />
            </div>
          </div>
          <div class="separator"></div>
        </template>
        <template v-if="display.smAndUp.value || actionsItems.length === 1">
          <!-- eslint-disable vuetify/no-deprecated-props -->
          <!-- see: https://github.com/vuetifyjs/eslint-plugin-vuetify/issues/59 -->
          <v-btn
            v-for="actionsItem in actionsItems"
            :key="actionsItem.id"
            variant="text"
            :prepend-icon="actionsItem.icon"
            color="white"
            :loading="actionsItem.isLoading"
            @click="actionsItem.click"
          >
            <span>
              {{ actionsItem.title }}
            </span>
            <!-- eslint-enable -->
          </v-btn>
        </template>
        <MenuItems
          v-else
          bg-color="black"
          :items="actionsItems"
          density="compact"
          @click:item="(item) => item.click()"
        >
          <template #activator="{ props: ActivatorProps }">
            <v-btn
              name="btn-preview-actions-menu"
              icon="far fa-ellipsis-vertical"
              size="small"
              slim
              variant="text"
              v-bind="ActivatorProps"
            />
          </template>
        </MenuItems>
      </template>
      <template v-else>
        <v-progress-circular indeterminate color="white" />
      </template>
    </v-card>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import { MIME_TYPES } from '@deepcloud/deep-ui-utils'
import MenuItems from './MenuItems.vue'

const props = defineProps({
  mimeType: {
    type: String,
    default: 'application/octet-stream',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isDownloading: {
    type: Boolean,
    default: false,
  },
  numPages: {
    type: [String, Number],
    default: null,
  },
  zoomPercentage: {
    type: [String, Number],
    default: null,
  },
  minZoom: {
    type: [String, Number],
    default: null,
  },
  maxZoom: {
    type: [String, Number],
    default: null,
  },
  zoom: {
    type: [String, Number],
    default: null,
  },
  currentPage: {
    type: [String, Number],
    default: null,
  },
  hideZoomPercentage: {
    type: Boolean,
    default: false,
  },
  hideZoom: {
    type: Boolean,
    default: false,
  },
})

const { t } = useI18n()
const display = useDisplay()

const emit = defineEmits([
  'click:download',
  'click:zoom-out',
  'click:zoom-in',
  'click:print',
])

const maxWidth = ref<number | undefined>(undefined)

function onResize(event: Event) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore TODO: check correct type
  maxWidth.value = event.currentTarget?.innerWidth
}

onMounted(() => {
  maxWidth.value = window.innerWidth
  window.addEventListener('resize', onResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
})

const actionsItems = computed(() => {
  const items = [
    {
      id: 'download',
      title: t('actions.download'),
      icon: 'far fa-file-arrow-down',
      isLoading: props.isDownloading,
      show: () => true,
      click: () => emit('click:download'),
    },
    {
      id: 'print',
      title: t('actions.print'),
      icon: 'far fa-print',
      show: () => props.mimeType === MIME_TYPES.MIME_TYPE_PDF,
      click: () => emit('click:print'),
    },
  ]

  return items.filter((i) => i.show())
})
</script>

<style lang="postcss" scoped>
.zoom-control {
  padding: 6px;
  justify-content: center;
}

.zoom-buttons-group {
  display: flex;
  flex-wrap: wrap;
  height: 34px;
  flex-direction: row;
  justify-content: center;
  align-content: center;
}

.separator {
  background-color: #868e96;
  width: 1px;
  height: 18px;
  margin: 0 8px;
}

.zoom-pages {
  color: white;
  margin-right: 2px;
  padding: 4px;
  font-size: 12px;
}

.fa::after {
  background-color: transparent;
}

.zoom-percentage {
  text-align: center;
  min-width: 34px;
  user-select: none;
}
</style>
